import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  ButtonGroup,
  Spinner,
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layouts/Main';
import config from '../config';
import { toast } from 'react-toastify';
import {
  FiRefreshCw,
  FiPlus,
  FiTruck,
  FiEye,
  FiSettings,
  FiAlertCircle
} from 'react-icons/fi';
import './OperationsDashboard.css';

import SalesOrdersTab from '../components/salesDashboard/SalesOrdersTab';
import DashboardModals from '../components/salesDashboard/DashboardModals';
import AllocationSuggestionModal from '../components/operationalDashboard/AllocationSuggestionModal';

// import { loggedInUserId } from '../utils/loggedInUserDetails';
import { useDashboardActions } from '../hooks/useDashboardActions';

const SalesOrderDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  // Allocation suggestion states
  const [pendingSuggestions, setPendingSuggestions] = useState([]);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState(null);

  // Load pending allocation suggestions
  const loadPendingSuggestions = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/allocation/suggestions/pending`
      );
      const suggestions = response.data.data || [];
      setPendingSuggestions(suggestions);

      // Auto-show modal if there are new suggestions and modal isn't already open
      if (suggestions.length > 0 && !showAllocationModal && !currentSuggestion) {
        // Check if this suggestion was dismissed today
        const dismissedToday = localStorage.getItem('dismissedAllocationSuggestions');
        const dismissedDate = dismissedToday ? new Date(dismissedToday).toDateString() : null;
        const today = new Date().toDateString();

        if (dismissedDate !== today) {
          setCurrentSuggestion(suggestions[0]);
          // setShowAllocationModal(true);
        }
      }
    } catch (error) {
      console.error('Error loading allocation suggestions:', error);
    }
  }, [showAllocationModal, currentSuggestion]);

  // Data states
  const [salesOrders, setSalesOrders] = useState({
    draft: [],
    new: [],
    inProduction: [],
    dispatched: []
  });
  const [reservedStock, setReservedStock] = useState({});
  const [stockSummary, setStockSummary] = useState([]);

  // Filter states
  const [soTabFilter, setSoTabFilter] = useState('all');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        soResponse,
        stockResponse,
      ] = await Promise.allSettled([
        axios.get(`${config.API_BASE_URL}/sales-orders?populateItems=true&populateBatch=true`),
        axios.get(`${config.API_BASE_URL}/inventory/stock-overview`),
      ]);

      if (soResponse.status === 'fulfilled') {
        console.log("hello");
        const allOrders = soResponse.value.data;
        setSalesOrders(allOrders);
      } else {
        setSalesOrders([]);
      }

      if (stockResponse.status === 'fulfilled' && stockResponse.value.data) {
        setStockSummary(stockResponse.value.data.products || []);
        setReservedStock(stockResponse.value.data.reservedStock || {});
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const {
    showBOMModal, setShowBOMModal,
    showProductionBatchModal, setShowProductionBatchModal,
    selectedSO, setSelectedSO,
    selectedItem, setSelectedItem,
    bomList,
    productionBatches,
    loadingBOM,
    loadingBatches,
    getSOStatusBadge,
    handleRaiseProductionRequest,
    handleCheckBOM,
    handleRunningProduction,
    handleCreatePurchaseRequest,
    handleCreateCommonPurchaseRequest,
    handleCreateFGPR,
    handleAssignToBatch
  } = useDashboardActions(fetchDashboardData);

  useEffect(() => {
    fetchDashboardData();
    loadPendingSuggestions();

    // Poll for suggestions every 30 seconds
    const interval = setInterval(loadPendingSuggestions, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData, loadPendingSuggestions]);

  const handleCreateProduction = (so) => {
    // Client-side navigation to production request creation page with pre-filled data (no reload)
    const salesOrderData = encodeURIComponent(JSON.stringify({
      salesOrderId: so._id,
      orderNumber: so.orderNumber,
      items: so.items
    }));
    navigate(`/production-requests/create?salesOrder=${salesOrderData}`);
  };

  const handleDispatchSO = (so) => {
    // Navigate to sales order details page with dispatch tab active
    navigate(`/sales-orders/${so._id}?tab=dispatch`);
  };

  const getActionButtons = (so) => {
    const buttons = [];

    if (so.status === 'Created' || so.status === 'Confirmed' || so.status === 'Finalized') {
      // Check if items have sufficient stock
      const hasStockIssues = so.items?.some(item => {
        const productStock = stockSummary.find(s => s.product._id === item.product._id);
        const availableStock = (productStock?.totalAvailableStock || 0) - (reservedStock[item.product._id] || 0);
        return availableStock < item.quantity;
      });

      if (hasStockIssues) {
        buttons.push(
          <Button
            key="production"
            variant="warning"
            size="sm"
            className="me-1"
            onClick={() => handleCreateProduction(so)}
          >
            <FiSettings className="me-1" />Production
          </Button>
        );
      } else {
        buttons.push(
          <Button
            key="dispatch"
            variant="success"
            size="sm"
            className="me-1"
            onClick={() => handleDispatchSO(so)}
          >
            <FiTruck className="me-1" />Dispatch
          </Button>
        );
      }
    }

    buttons.push(
      <Link
        key="view"
        to={`/sales-orders/${so._id}`}
        className="btn btn-outline-primary btn-sm"
      >
        <FiEye className="me-1" />View
      </Link>
    );

    return <ButtonGroup>{buttons}</ButtonGroup>;
  };


  // // Handler for allocation modal close
  // const handleAllocationModalClose = () => {
  //   localStorage.setItem('dismissedAllocationSuggestions', new Date().toISOString());
  //   setShowAllocationModal(false);
  //   setCurrentSuggestion(null);
  // };

  // // Handler for allocation success
  // const handleAllocationSuccess = async () => {
  //   setShowAllocationModal(false);
  //   setCurrentSuggestion(null);
  //   toast.success('Stock allocated successfully');
  //   await fetchDashboardData();
  //   await loadPendingSuggestions();
  // };

  if (loading) {
    return (
      <Layout title="Sales Dashboard">
        <Container fluid className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading Sales Order Dashboard...</p>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Sales Dashboard">
      <Container fluid className="py-4 operations-dashboard">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">Sales Order Dashboard</h2>
                <p className="text-muted mb-0">
                  Manage all sales orders and related operations
                </p>
              </div>
              <div className="d-flex gap-2">
                {pendingSuggestions.length > 0 && (
                  <Button
                    variant="warning"
                    onClick={() => {
                      setCurrentSuggestion(pendingSuggestions[0]);
                      setShowAllocationModal(true);
                    }}
                  >
                    <FiAlertCircle className="me-1" />
                    {/* {pendingSuggestions.length}  */}
                    Stock Allocation{pendingSuggestions.length > 1 ? 's' : ''} Pending
                  </Button>
                )}
                <Button
                  variant="outline-primary"
                  onClick={fetchDashboardData}
                  disabled={loading}
                >
                  <FiRefreshCw className="me-1" />Refresh
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/sales-orders/create')}
                >
                  <FiPlus className="me-1" />Create Sales Order
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Panels - render based on activeTab */}
        <div>
          <SalesOrdersTab
            salesOrders={salesOrders}
            soTabFilter={soTabFilter}
            setSoTabFilter={setSoTabFilter}
            stockSummary={stockSummary}
            reservedStock={reservedStock}
            getSOStatusBadge={getSOStatusBadge}
            getActionButtons={getActionButtons}
            handleCheckBOM={handleCheckBOM}
            handleRunningProduction={handleRunningProduction}
            fetchDashboardData={fetchDashboardData}
          />
        </div>

        <DashboardModals
          selectedSO={selectedSO}
          showBOMModal={showBOMModal}
          setShowBOMModal={setShowBOMModal}
          selectedItem={selectedItem}
          loadingBOM={loadingBOM}
          bomList={bomList}
          stockSummary={stockSummary}
          handleCreatePurchaseRequest={handleCreatePurchaseRequest}
          handleCreateCommonPurchaseRequest={handleCreateCommonPurchaseRequest}
          handleCreateFGPR={handleCreateFGPR}
          showProductionBatchModal={showProductionBatchModal}
          setShowProductionBatchModal={setShowProductionBatchModal}
          loadingBatches={loadingBatches}
          productionBatches={productionBatches}
          handleAssignToBatch={handleAssignToBatch}
          handleRaiseProductionRequest={handleRaiseProductionRequest}
        />

        {/* Allocation Suggestion Modal */}
        {currentSuggestion && (
          <AllocationSuggestionModal
            show={showAllocationModal}
            onHide={() => {
              setShowAllocationModal(false);
              setCurrentSuggestion(null);
              loadPendingSuggestions();
            }}
            suggestion={currentSuggestion}
            onAllocate={async (allocations) => {
              try {
                await axios.post(
                  `${config.API_BASE_URL}/allocation/suggestions/${currentSuggestion._id}/allocate`,
                  { allocations }
                );
                toast.success('Stock allocated successfully');
                setShowAllocationModal(false);
                setCurrentSuggestion(null);
                await fetchDashboardData();
                await loadPendingSuggestions();
              } catch (error) {
                console.error('Allocation error:', error);
                toast.error(error.response?.data?.message || 'Failed to allocate stock');
              }
            }}
            onDismiss={async () => {
              try {
                await axios.post(
                  `${config.API_BASE_URL}/allocation/suggestions/${currentSuggestion._id}/dismiss`
                );
                setShowAllocationModal(false);
                setCurrentSuggestion(null);
                await loadPendingSuggestions();
              } catch (error) {
                console.error('Dismiss error:', error);
                toast.error('Failed to dismiss suggestion');
              }
            }}
          />
        )}
      </Container>
    </Layout>
  );
};

export default SalesOrderDashboard;
